import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { zegoEngine } from '../../zegoCallConfig';
import { loginToZego } from '../../components/utils/zegoLoginVC';

import { FiMic, FiMicOff, FiVideo, FiVideoOff, FiMessageSquare } from 'react-icons/fi';
import { IoCall, IoAttach, IoCameraReverseOutline } from 'react-icons/io5';
import axios from '../../api/axios';

interface TeleconsultationData {
  patient_id: string;
  patient_name: string;
  practitioner_id: string;
  practitioner_name: string;
}
interface UserData {
  id: string;
  person: {
    name: string;
  };
  practitioner_role_id: string;
}

type ZegoRemoteStream = {
  streamID: string;
  fromUser?: { userID: string; userName?: string };
  cameraState?: 'OPEN' | 'MUTE'; 
  micState?: 'OPEN' | 'MUTE';
};

type ZegoRemoteStreamWithMedia = ZegoRemoteStream & {
  mediaStream: MediaStream;
};

const VideoCallUI: React.FC = () => {
  const navigate = useNavigate();
  const { teleconsultationId, token } = useParams<{ 
    teleconsultationId: string; 
    token: string; 
  }>();

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  const currentRemoteStreamID = useRef<string | null>(null);
  const myStreamID = useRef<string>('');

  // State
  const [isJoined, setIsJoined] = useState(false);
  const [isCallStarted, setIsCallStarted] = useState(false);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [availableStreams, setAvailableStreams] = useState<ZegoRemoteStreamWithMedia[]>([]);

  const [userData, setUserData] = useState<UserData | null>(null);
  const [teleconsultationData, setTeleconsultationData] = useState<TeleconsultationData | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Validation functions
  const isValidUserData = (data: any): data is UserData => {
    return data && data.id && data.person?.name && data.practitioner_role_id;
  };
  const isValidTeleconsultationData = (data: any): data is TeleconsultationData => {
    return data && data.patient_id && data.patient_name && data.practitioner_id;
  };

  // UI state
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [remoteAutoMuted, setRemoteAutoMuted] = useState(true);

  // State Fitur Baru
  const [isLocalVideoMain, setIsLocalVideoMain] = useState(false);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentDeviceID, setCurrentDeviceID] = useState<string | undefined>();

  const [remoteStreamStates, setRemoteStreamStates] = useState<Record<string, 'OPEN' | 'MUTE'>>({});


  const zg = zegoEngine;

  // Timer panggilan
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (isCallStarted) {
      timer = setInterval(() => setCallDuration((t) => t + 1), 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
      setCallDuration(0);
    };
  }, [isCallStarted]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const handleNavigationError = useCallback((error: unknown) => {
    if (isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 400)) {
      navigate('/not-found');
    } else {
      console.error('Unexpected error:', error);
    }
  }, [navigate]);

  // API calls
  const fetchUserData = useCallback(async (): Promise<UserData | null> => {
    if (!token) return null;
    try {
      const response = await axios.post('users', null, { headers: { Authorization: `Bearer ${token}` } });
      const data = response.data.data;
      sessionStorage.setItem('userData', JSON.stringify(data));
      return data;
    } catch (error) {
      handleNavigationError(error);
      return null;
    }
  }, [token, handleNavigationError]);

  const fetchTeleconsultationData = useCallback(async (): Promise<TeleconsultationData | null> => {
    if (!token || !teleconsultationId) return null;
    try {
      const response = await axios.get(`teleconsultation/${teleconsultationId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = response.data;
      sessionStorage.setItem('teleconsultationData', JSON.stringify(data));
      return data;
    } catch (error) {
      handleNavigationError(error);
      return null;
    }
  }, [token, teleconsultationId, handleNavigationError]);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const savedUserData = sessionStorage.getItem('userData');
        const savedTeleconsultationData = sessionStorage.getItem('teleconsultationData');
        let userDataValid = false;
        let teleconsultationDataValid = false;
        if (savedUserData) {
          try {
            const parsedUserData = JSON.parse(savedUserData);
            if (isValidUserData(parsedUserData)) {
              setUserData(parsedUserData);
              userDataValid = true;
            }
          } catch (e) { console.warn('Corrupt UserData in session'); }
        }
        if (savedTeleconsultationData) {
          try {
            const parsedTeleconsultationData = JSON.parse(savedTeleconsultationData);
            if (isValidTeleconsultationData(parsedTeleconsultationData)) {
              setTeleconsultationData(parsedTeleconsultationData);
              teleconsultationDataValid = true;
            }
          } catch (e) { console.warn('Corrupt TeleconsultationData in session'); }
        }
        if (!userDataValid || !teleconsultationDataValid) {
          const promises = [];
          if (!userDataValid) promises.push(fetchUserData());
          if (!teleconsultationDataValid) promises.push(fetchTeleconsultationData());
          const results = await Promise.all(promises);
          let fetchIndex = 0;
          if (!userDataValid && results[fetchIndex]) {
            setUserData(results[fetchIndex] as UserData);
            fetchIndex++;
          }
          if (!teleconsultationDataValid && results[fetchIndex]) {
            setTeleconsultationData(results[fetchIndex] as TeleconsultationData);
          }
        }
        setIsDataLoaded(true);
      } catch (error) {
        console.error('Error loading initial data:', error);
        setIsDataLoaded(true);
      }
    };
    loadInitialData();
  }, [fetchUserData, fetchTeleconsultationData]);

  // Otomatis join room setelah data siap
  useEffect(() => {
    const joinRoomAutomatically = async () => {
      if (isDataLoaded && teleconsultationData && userData && !isJoined) {
        const envSuffix = process.env.REACT_APP_ZEGOCLOUD_APP_ENV || '';
        const finalUserID = `LMS${teleconsultationData.patient_id}${envSuffix}`;
        const finalUserName = teleconsultationData.patient_name;
        const finalRoomID = teleconsultationId!;
        const finalStreamID = `stream_${finalRoomID}_${finalUserID}`;

        const ok = await loginToZego(finalUserID, finalUserName, finalRoomID);
        if (ok) {
          myStreamID.current = finalStreamID;
          setIsJoined(true);
        } else {
          alert('Gagal bergabung ke dalam room.');
        }
      }
    };
    joinRoomAutomatically();
  }, [isDataLoaded, teleconsultationData, userData, isJoined, teleconsultationId]);

// useEffect untuk mendengarkan perubahan status stream remote (kamera & mic)
  useEffect(() => {
    // Handler dengan logika yang benar untuk event 'remoteStreamUpdated'
    const handleRemoteStreamUpdated = (
      updateType: 'CAMERA' | 'MIC',
      streamList: ZegoRemoteStream[]
    ) => {
      if (updateType === 'CAMERA') {
        const newStates: Record<string, 'OPEN' | 'MUTE'> = {};
        streamList.forEach(stream => {
          const state = stream.cameraState === 'OPEN' ? 'OPEN' : 'MUTE';
          newStates[stream.streamID] = state;
        });

        setRemoteStreamStates(prevStates => ({
          ...prevStates,
          ...newStates
        }));
      }
    };

    // Daftarkan event listener dengan NAMA EVENT YANG BENAR
    (zg as any).on('remoteStreamUpdated', handleRemoteStreamUpdated);

    // Hapus listener saat komponen dibongkar
    // FIX: Gunakan kurung kurawal {} agar fungsi ini mengembalikan 'void'
    return () => {
    (zg as any).off('remoteStreamUpdated', handleRemoteStreamUpdated);
    };
  }, [zg]); // Dependensi sudah benar

  
  // Handler event Zego
  const handleRoomStreamUpdate = useCallback(async (
    _roomID: string,
    updateType: 'ADD' | 'DELETE',
    streamList: ZegoRemoteStream[]
  ) => {
    if (updateType === 'ADD') {
        const playPromises = streamList
            .filter(streamInfo => streamInfo.streamID !== myStreamID.current)
            .map(async streamInfo => {
                try {
                    const remoteStream = await zg.startPlayingStream(streamInfo.streamID, {
                        audio: true, video: true,
                    });
                    return { ...streamInfo, mediaStream: remoteStream as unknown as MediaStream };
                } catch (err) {
                    console.error(`Gagal memutar stream remote ${streamInfo.streamID}:`, err);
                    return null;
                }
            });
        const newStreams = (await Promise.all(playPromises)).filter(Boolean) as ZegoRemoteStreamWithMedia[];
        if (newStreams.length > 0) {
            setAvailableStreams(prevStreams => {
                const existingStreamIDs = new Set(prevStreams.map(s => s.streamID));
                const uniqueNewStreams = newStreams.filter(s => !existingStreamIDs.has(s.streamID));
                return [...prevStreams, ...uniqueNewStreams];
            });
        }
    } else if (updateType === 'DELETE') {
        const deletedIDs = streamList.map((s) => s.streamID);
        deletedIDs.forEach((id) => {
            try { zg.stopPlayingStream(id); } catch { }
        });
        setAvailableStreams((prev) => prev.filter((s) => !deletedIDs.includes(s.streamID)));
    }
  }, [zg]);

  useEffect(() => {
    zg.on('roomStreamUpdate', handleRoomStreamUpdate);
    return () => {
      zg.off('roomStreamUpdate', handleRoomStreamUpdate);
    };
  }, [zg, handleRoomStreamUpdate]);

  // Efek untuk memasang stream remote ke video element
  useEffect(() => {
    if (!isCallStarted) return;
    const target = availableStreams.find((s) => s.streamID !== myStreamID.current) || null;
    if (target && target.streamID === currentRemoteStreamID.current) return;
    if (!target) {
        if (currentRemoteStreamID.current && remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
            currentRemoteStreamID.current = null;
        }
        return;
    }
    if (target && remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = target.mediaStream;
        remoteVideoRef.current.play().catch((err) => {
            console.error('Gagal autoplay video remote:', err);
        });
        currentRemoteStreamID.current = target.streamID;
    }
  }, [availableStreams, isCallStarted]);
  
  // Efek untuk unmute remote video setelah user gesture
  useEffect(() => {
    const videoEl = remoteVideoRef.current;
    if (videoEl && currentRemoteStreamID.current) {
      videoEl.muted = remoteAutoMuted;
    }
  }, [remoteAutoMuted]);

  // Efek untuk memulai stream lokal
  useEffect(() => {
    if (!isCallStarted) return;
    let stream: MediaStream | null = null;
    const startLocal = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setVideoDevices(videoDevices);

        stream = await zg.createStream({
          camera: { audio: true, video: true }
        });
        setLocalStream(stream);

        const videoTracks = stream.getVideoTracks();
        if (videoTracks.length > 0) {
            const currentSettings = videoTracks[0].getSettings();
            if (currentSettings.deviceId) {
                setCurrentDeviceID(currentSettings.deviceId);
            }
        }
        
        if (!isCameraOff && localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          await localVideoRef.current.play().catch(() => {});
        }
        zg.startPublishingStream(myStreamID.current, stream);
      } catch (err) {
        console.error('Error memulai stream lokal:', err);
      }
    };
    startLocal();
    return () => {
        if (stream) {
            try { zg.destroyStream(stream); } catch (e) { }
        }
    }
  }, [isCallStarted]); // Dijalankan hanya sekali saat call dimulai

  // Efek untuk menukar video DAN mengelola pratinjau lokal saat kamera on/off
  useEffect(() => {
    const remoteStreamTarget = availableStreams.find(s => s.streamID !== myStreamID.current) || null;
    const remoteStream = remoteStreamTarget ? remoteStreamTarget.mediaStream : null;
    
    // Logika untuk video utama (remote) tidak banyak berubah
    if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = isLocalVideoMain ? localStream : remoteStream;
    }

    // Logika untuk video pratinjau (lokal)
    if (localVideoRef.current) {
        // Jika kamera sedang off, jangan pasang stream apapun (ini akan ditangani oleh JSX)
        // Namun, saat kamera ON lagi (isCameraOff=false), kita harus memasang stream-nya.
        if (isCameraOff && !isLocalVideoMain) {
            localVideoRef.current.srcObject = null;
        } else {
            localVideoRef.current.srcObject = isLocalVideoMain ? remoteStream : localStream;
        }
    }
  }, [isLocalVideoMain, localStream, availableStreams, myStreamID, isCameraOff]);

  const handleStartCall = () => {
    setIsCallStarted(true);
    setRemoteAutoMuted(false);
  };
  
  const handleSwapVideo = () => {
    setIsLocalVideoMain(prevState => !prevState);
  };
  
  const handleSwitchCamera = async () => {
    if (!localStream || videoDevices.length < 2) return;
    try {
        const currentDeviceIndex = videoDevices.findIndex(d => d.deviceId === currentDeviceID);
        const nextDeviceIndex = (currentDeviceIndex + 1) % videoDevices.length;
        const nextDevice = videoDevices[nextDeviceIndex];
        if (nextDevice) {
            await zg.useVideoDevice(localStream, nextDevice.deviceId);
            setCurrentDeviceID(nextDevice.deviceId);
        }
    } catch (err) {
        console.error('Gagal beralih kamera:', err);
    }
  };

  const handleLeaveCall = async () => {
    try {
      if (localStream) {
        try { zg.stopPublishingStream(myStreamID.current); } catch {}
        try { zg.destroyStream(localStream); } catch {}
      }
      availableStreams.forEach(s => {
        try { zg.stopPlayingStream(s.streamID); } catch {}
      });
      if (teleconsultationId) {
        await zg.logoutRoom(teleconsultationId);
      }
    } catch (e) {
      console.error('Gagal saat keluar dari room:', e);
    } finally {
      // Reset semua state ke awal
      setIsJoined(false);
      setIsCallStarted(false);
      setLocalStream(null);
      setAvailableStreams([]);
      setCallDuration(0);
      currentRemoteStreamID.current = null;
      setRemoteAutoMuted(true);
      navigate(-1); // Kembali ke halaman sebelumnya
    }
  };

  const toggleAudio = () => {
    if (!localStream) return;
    const isMuting = !isAudioMuted;
    zg.mutePublishStreamAudio(localStream, isMuting);
    setIsAudioMuted(isMuting);
  };
  
  // ==========================================================
  // ## FUNGSI YANG DIPERBAIKI ##
  // ==========================================================
  const toggleVideo = () => {
    if (!localStream) return;

    const isTurningOn = isCameraOff; // Jika state saat ini 'off' (true), berarti kita mau 'on'

    // Kunci utama: Kelola 'enabled' state pada video track
    // Ini akan mengirim frame hitam saat 'false' dan frame kamera saat 'true'
    localStream.getVideoTracks().forEach((track) => {
      track.enabled = isTurningOn;
    });

    // Optimasi: Beri tahu Zego untuk tidak mengirim data saat di-mute
    const isMuting = !isTurningOn;
    zg.mutePublishStreamVideo(localStream, isMuting);

    // Perbarui state React
    setIsCameraOff(isMuting);
  };
  // ==========================================================
  
  // ----- RENDER -----
  if (!isDataLoaded || !isJoined) {
    return (
        <div style={styles.readyScreen}>
            <p>Menyambungkan ke layanan telekonsultasi...</p>
        </div>
    );
  }

  if (!isCallStarted) {
    return (
        <div style={styles.readyScreen}>
            <p>Siap untuk memulai panggilan di Room: {teleconsultationId}</p>
            <p>Anda akan terhubung dengan {teleconsultationData?.practitioner_name || 'Dokter'}</p>
            <button style={styles.joinButton} onClick={handleStartCall}>Mulai Panggilan</button>
        </div>
    );
  }

   // Cek status kamera remote (logika ini tetap sama)
  const currentRemoteID = currentRemoteStreamID.current;
  const isRemoteCameraOff = currentRemoteID ? remoteStreamStates[currentRemoteID] === 'MUTE' : false;
  
  return (
    <div style={styles.callContainer}>
        <video 
          ref={remoteVideoRef} 
          style={styles.remoteVideo} 
          autoPlay 
          playsInline
        />

        {/* PERUBAHAN DI SINI: Overlay ikon hanya muncul saat kamera remote off */}
        {isRemoteCameraOff && (
            <div style={styles.remoteCameraOffOverlay}>
                <FiVideoOff size={60} color="#fff" />
            </div>
        )}

        <div style={styles.videoOverlay}></div>

        <div style={styles.doctorInfo}>
            <p style={{ margin: 0, fontWeight: 'bold' }}>{teleconsultationData?.practitioner_name || 'Guest'}</p>
            <p style={{ margin: 0, fontSize: '0.9em' }}>
                <span style={{ color: 'red', marginRight: 5 }}>●</span>
                {formatDuration(callDuration)}
            </p>
        </div>

        <div 
          style={{...styles.localVideoContainer, cursor: 'pointer'}} 
          onClick={handleSwapVideo}
        >
            {isCameraOff && !isLocalVideoMain ? (
                <div style={styles.cameraOffPlaceholder}><FiVideoOff size={40} color="#fff" /></div>
            ) : (
                <video 
                    ref={localVideoRef} 
                    style={styles.localVideo} 
                    autoPlay 
                    playsInline 
                    muted 
                />
            )}
        </div>

        <div style={styles.controlsBar}>
            <button style={styles.controlButton} onClick={() => alert('Chat clicked')}><FiMessageSquare size={24} color="#E84142" /></button>
            <button style={styles.controlButton} onClick={() => alert('Attach clicked')}><IoAttach size={24} color="#E84142" /></button>
            <button style={styles.controlButton} onClick={toggleAudio}>
                {isAudioMuted ? <FiMicOff size={24} color="#E84142" /> : <FiMic size={24} color="#E84142" />}
            </button>
            <button style={styles.controlButton} onClick={toggleVideo}>
                {isCameraOff ? <FiVideoOff size={24} color="#E84142" /> : <FiVideo size={24} color="#E84142" />}
            </button>

            {videoDevices.length > 1 && (
                <button style={styles.controlButton} onClick={handleSwitchCamera}>
                    <IoCameraReverseOutline size={24} color="#E84142" />
                </button>
            )}

            <button style={{ ...styles.controlButton, background: '#E84142' }} onClick={handleLeaveCall}>
                <IoCall size={24} color="white" style={{ transform: 'rotate(135deg)' }} />
            </button>
        </div>
    </div>
  );
};

// Objek Styles
const styles: { [key: string]: React.CSSProperties } = {
    formContainer: { display: 'flex', flexDirection: 'column', padding: 20, gap: 10 },
    joinButton: {
        padding: '10px 20px',
        background: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
    },
    readyScreen: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 20,
        textAlign: 'center',
        padding: '20px'
    },
    callContainer: { width: '100%', height: '100vh', background: 'black', position: 'relative', overflow: 'hidden' },
    remoteVideo: { width: '100%', height: '100%', objectFit: 'cover' },
    videoOverlay: { position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 40%)' },
    doctorInfo: {
        position: 'absolute',
        top: 50,
        left: 20,
        background: 'rgba(255, 255, 255, 0.9)',
        padding: '8px 16px',
        borderRadius: 12,
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        color: '#333'
    },
    localVideoContainer: {
        position: 'absolute',
        top: 50,
        right: 20,
        width: 100,
        height: 150,
        borderRadius: 12,
        background: '#333',
        overflow: 'hidden',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    },
    localVideo: { width: '100%', height: '100%', objectFit: 'cover' },
    cameraOffPlaceholder: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#444' },
    controlsBar: {
        position: 'absolute',
        bottom: 40,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 15,
        background: 'white',
        padding: '10px 20px',
        borderRadius: 50,
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
    },
    controlButton: {
        width: 50,
        height: 50,
        borderRadius: '50%',
        border: '1px solid #eee',
        background: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    remoteCameraOffOverlay: {
        position: 'absolute',
        inset: 0, // Shorthand untuk top: 0, right: 0, bottom: 0, left: 0
        background: 'rgba(0, 0, 0, 0.5)', // Latar belakang semi-transparan
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1, // Pastikan overlay di atas video
    },
};

export default VideoCallUI;